import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { TypeMetadataFields } from './metadata.model';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MetadataService {
  private meta!: TypeMetadataFields;
  private renderer: Renderer2;
  private isBrowser: boolean;

  constructor(
    private title: Title,
    private metadata: Meta,
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    @Inject(PLATFORM_ID) platformId: string
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.isBrowser = isPlatformBrowser(platformId);
  }

  getFullUrl(): string {
    const path = this.router.url;
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const fragment = this.activatedRoute.snapshot.fragment;

    if (!this.isBrowser) {
      return `${environment.SITE_URL}${path}`;
    }

    const protocol = window.location.protocol;
    const host = window.location.host;

    let fullUrl = `${protocol}//${host}${path}`;

    if (Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString();
      fullUrl += `?${queryString}`;
    }

    if (fragment) {
      fullUrl += `#${fragment}`;
    }

    return fullUrl;
  }

  setMetadata(meta?: TypeMetadataFields): void {
    if (!meta) return;
    this.meta = meta;

    this.setTitle();
    this.setDescription();
    this.setRobots();
    this.setSocialMetadata();
  }

  private setTitle(): void {
    this.title.setTitle(this.meta.title);
  }

  private setDescription(): void {
    this.metadata.updateTag({ name: 'description', content: this.meta.description });
  }

  updateCanonicalLink(newCanonicalUrl: string): void {
    let canonicalLink = this.document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      this.renderer.setAttribute(canonicalLink, 'href', newCanonicalUrl);
    } else {
      canonicalLink = this.renderer.createElement('link');
      this.renderer.setAttribute(canonicalLink, 'rel', 'canonical');
      this.renderer.setAttribute(canonicalLink, 'href', newCanonicalUrl);
      this.renderer.appendChild(this.document.head, canonicalLink);
    }

    this.metadata.updateTag({ property: 'og:url', content: newCanonicalUrl });
    this.metadata.updateTag({ name: 'twitter:url', content: newCanonicalUrl });
  }

  private setRobots(): void {
    const follow = this.meta.follow ? 'follow' : 'nofollow';
    const index = this.meta.index ? 'index' : 'noindex';

    this.metadata.updateTag({ name: 'robots', content: `${index}, ${follow}` });
  }

  private setSocialMetadata(): void {
    this.metadata.updateTag({ property: 'og:title', content: this.meta.title });
    this.metadata.updateTag({ property: 'og:description', content: this.meta.description });
    this.metadata.updateTag({ name: 'twitter:title', content: this.meta.title });
    this.metadata.updateTag({ name: 'twitter:description', content: this.meta.description });
  }
}
